import { memo, useMemo } from 'react';
import { Polygon, Polyline, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import './AirportDetailOverlay.css';

// Feature styling for different airport elements
const AIRPORT_FEATURE_STYLES = {
  runway: {
    fillColor: '#4a4a4a',
    fillOpacity: 0.9,
    color: '#ffffff',
    weight: 2,
    opacity: 0.8
  },
  taxiway: {
    fillColor: '#5a5a5a',
    fillOpacity: 0.7,
    color: '#888888',
    weight: 1,
    opacity: 0.6
  },
  apron: {
    fillColor: '#666666',
    fillOpacity: 0.5,
    color: '#888888',
    weight: 1,
    opacity: 0.5
  },
  terminal: {
    fillColor: '#8b4513',
    fillOpacity: 0.7,
    color: '#654321',
    weight: 2,
    opacity: 0.8
  },
  hangar: {
    fillColor: '#696969',
    fillOpacity: 0.6,
    color: '#505050',
    weight: 1,
    opacity: 0.7
  }
};

// Create a small icon for parking positions/stands
const createStandIcon = (ref) => {
  return L.divIcon({
    className: 'airport-stand-marker',
    html: `<div class="stand-label">${ref || ''}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12]
  });
};

// Create runway number icon styled like real runway markings
const createRunwayNumberIcon = (number, rotation) => {
  return L.divIcon({
    className: 'runway-number-marker',
    html: `<div class="runway-number" style="transform: rotate(${rotation}deg)">${number}</div>`,
    iconSize: [32, 20],
    iconAnchor: [16, 10]
  });
};

// Calculate runway endpoints and numbers from coordinates
const calculateRunwayEndpoints = (runway) => {
  if (!runway.coordinates || runway.coordinates.length < 2) return null;

  // Get the runway polygon coordinates
  const coords = runway.coordinates;

  // Find the two endpoints (typically the short edges of the runway rectangle)
  // For a runway rectangle, we want the midpoints of the short edges
  // Simplification: use the runway name to determine heading, then calculate endpoints

  // Parse runway name (e.g., "09/27", "09L/27R")
  const runwayName = runway.name || runway.ref || '';
  const match = runwayName.match(/(\d{1,2}[LRC]?)(?:\/(\d{1,2}[LRC]?))?/);

  if (!match) return null;

  const rwy1 = match[1];
  const rwy2 = match[2] || null;

  // Calculate heading from runway number (multiply by 10)
  const heading1 = parseInt(rwy1.replace(/[LRC]/g, '')) * 10;
  const heading2 = rwy2 ? parseInt(rwy2.replace(/[LRC]/g, '')) * 10 : (heading1 + 180) % 360;

  // Find runway length by finding max distance between any two points
  let maxDist = 0;
  let endpoint1 = coords[0];
  let endpoint2 = coords[1];

  for (let i = 0; i < coords.length; i++) {
    for (let j = i + 1; j < coords.length; j++) {
      const dist = Math.sqrt(
        Math.pow(coords[i][0] - coords[j][0], 2) +
        Math.pow(coords[i][1] - coords[j][1], 2)
      );
      if (dist > maxDist) {
        maxDist = dist;
        endpoint1 = coords[i];
        endpoint2 = coords[j];
      }
    }
  }

  // Determine which endpoint corresponds to which runway number
  // Based on the bearing between the two points
  const bearing = Math.atan2(
    endpoint2[1] - endpoint1[1],
    endpoint2[0] - endpoint1[0]
  ) * 180 / Math.PI;

  // Normalize bearing to 0-360
  const normalizedBearing = ((bearing + 360) % 360);

  // Determine if endpoint1 is the lower numbered runway
  const diff1 = Math.abs(normalizedBearing - heading1);
  const diff2 = Math.abs(normalizedBearing - heading2);

  // Offset the labels beyond the runway ends
  // Calculate direction vectors and extend by ~7.5% of runway length
  const offsetFactor = 0.075;
  const dirLat = endpoint2[0] - endpoint1[0];
  const dirLon = endpoint2[1] - endpoint1[1];

  // Offset positions beyond each end
  const offset1Lat = endpoint1[0] - dirLat * offsetFactor;
  const offset1Lon = endpoint1[1] - dirLon * offsetFactor;
  const offset2Lat = endpoint2[0] + dirLat * offsetFactor;
  const offset2Lon = endpoint2[1] + dirLon * offsetFactor;

  // Rotation should match the runway heading so the number is readable
  // from the approach direction (facing incoming aircraft)
  let result;
  if (diff1 < diff2 || diff1 > 270) {
    result = {
      end1: { lat: offset1Lat, lon: offset1Lon, number: rwy1, rotation: heading1 },
      end2: { lat: offset2Lat, lon: offset2Lon, number: rwy2 || formatOppositeRunway(rwy1), rotation: heading2 }
    };
  } else {
    result = {
      end1: { lat: offset1Lat, lon: offset1Lon, number: rwy2 || formatOppositeRunway(rwy1), rotation: heading2 },
      end2: { lat: offset2Lat, lon: offset2Lon, number: rwy1, rotation: heading1 }
    };
  }

  return result;
};

// Format opposite runway number
const formatOppositeRunway = (rwy) => {
  const num = parseInt(rwy.replace(/[LRC]/g, ''));
  const suffix = rwy.match(/[LRC]/)?.[0] || '';
  const opposite = num <= 18 ? num + 18 : num - 18;
  const oppositeSuffix = suffix === 'L' ? 'R' : suffix === 'R' ? 'L' : suffix;
  return `${opposite.toString().padStart(2, '0')}${oppositeSuffix}`;
};

function AirportDetailOverlay({ features, airport, pane = 'airportFeaturesPane', zoomLevel = 'airport' }) {
  // Memoize stand icons to prevent recreation on every render
  // Must be called before any early returns to follow React hooks rules
  const standIcons = useMemo(() => {
    if (!features?.parkingPositions) return {};
    const icons = {};
    features.parkingPositions.forEach(stand => {
      icons[stand.id] = createStandIcon(stand.ref);
    });
    return icons;
  }, [features?.parkingPositions]);

  // Memoize runway endpoint calculations and icons
  const runwayEndpoints = useMemo(() => {
    if (!features?.runways) return [];
    return features.runways
      .map(runway => ({
        runway,
        endpoints: calculateRunwayEndpoints(runway)
      }))
      .filter(item => item.endpoints !== null);
  }, [features?.runways]);

  const runwayIcons = useMemo(() => {
    const icons = {};
    runwayEndpoints.forEach(({ runway, endpoints }) => {
      if (endpoints) {
        icons[`${runway.id}-1`] = createRunwayNumberIcon(endpoints.end1.number, endpoints.end1.rotation);
        icons[`${runway.id}-2`] = createRunwayNumberIcon(endpoints.end2.number, endpoints.end2.rotation);
      }
    });
    return icons;
  }, [runwayEndpoints]);

  // Don't render if no features
  if (!features) {
    return null;
  }

  return (
    <>
      {/* Render in z-order: aprons (bottom) -> taxiways -> runways (top) */}

      {/* Aprons / Parking Areas */}
      {features.aprons?.map(apron => (
        <Polygon
          key={apron.id}
          positions={apron.coordinates}
          pathOptions={AIRPORT_FEATURE_STYLES.apron}
          pane={pane}
        >
          {apron.name && (
            <Tooltip sticky>
              <div className="airport-feature-tooltip">
                <strong>Apron</strong>
                {apron.name && <div>{apron.name}</div>}
              </div>
            </Tooltip>
          )}
        </Polygon>
      ))}

      {/* Hangars */}
      {features.hangars?.map(hangar => (
        <Polygon
          key={hangar.id}
          positions={hangar.coordinates}
          pathOptions={AIRPORT_FEATURE_STYLES.hangar}
          pane={pane}
        >
          <Tooltip sticky>
            <div className="airport-feature-tooltip">
              <strong>Hangar</strong>
              {hangar.name && <div>{hangar.name}</div>}
            </div>
          </Tooltip>
        </Polygon>
      ))}

      {/* Terminal Buildings */}
      {features.terminals?.map(terminal => (
        <Polygon
          key={terminal.id}
          positions={terminal.coordinates}
          pathOptions={AIRPORT_FEATURE_STYLES.terminal}
          pane={pane}
        >
          <Tooltip sticky>
            <div className="airport-feature-tooltip">
              <strong>Terminal</strong>
              {terminal.name && <div>{terminal.name}</div>}
            </div>
          </Tooltip>
        </Polygon>
      ))}

      {/* Taxiways - can be polygons or polylines depending on OSM data */}
      {features.taxiways?.map(taxiway => {
        // If taxiway has width data, render as polygon; otherwise as polyline
        const isPolygon = taxiway.coordinates.length > 2 &&
          taxiway.coordinates[0][0] === taxiway.coordinates[taxiway.coordinates.length - 1][0] &&
          taxiway.coordinates[0][1] === taxiway.coordinates[taxiway.coordinates.length - 1][1];

        if (isPolygon) {
          return (
            <Polygon
              key={taxiway.id}
              positions={taxiway.coordinates}
              pathOptions={AIRPORT_FEATURE_STYLES.taxiway}
              pane={pane}
            >
              {taxiway.ref && (
                <Tooltip sticky>
                  <div className="airport-feature-tooltip">
                    <strong>Taxiway {taxiway.ref}</strong>
                    {taxiway.surface && <div>Surface: {taxiway.surface}</div>}
                  </div>
                </Tooltip>
              )}
            </Polygon>
          );
        }

        return (
          <Polyline
            key={taxiway.id}
            positions={taxiway.coordinates}
            pathOptions={{
              color: AIRPORT_FEATURE_STYLES.taxiway.fillColor,
              weight: 4,
              opacity: 0.7
            }}
            pane={pane}
          >
            {taxiway.ref && (
              <Tooltip sticky>
                <div className="airport-feature-tooltip">
                  <strong>Taxiway {taxiway.ref}</strong>
                  {taxiway.surface && <div>Surface: {taxiway.surface}</div>}
                </div>
              </Tooltip>
            )}
          </Polyline>
        );
      })}

      {/* Runways - rendered on top */}
      {features.runways?.map(runway => (
        <Polygon
          key={runway.id}
          positions={runway.coordinates}
          pathOptions={AIRPORT_FEATURE_STYLES.runway}
          pane={pane}
        >
          <Tooltip sticky>
            <div className="airport-feature-tooltip">
              <strong>Runway {runway.name || runway.ref || ''}</strong>
              {runway.surface && <div>Surface: {runway.surface}</div>}
              {runway.length && <div>Length: {runway.length}m</div>}
              {runway.width && <div>Width: {runway.width}m</div>}
            </div>
          </Tooltip>
        </Polygon>
      ))}

      {/* Runway Numbers */}
      {runwayEndpoints.map(({ runway, endpoints }) => (
        endpoints && (
          <span key={`rwy-nums-${runway.id}`}>
            <Marker
              position={[endpoints.end1.lat, endpoints.end1.lon]}
              icon={runwayIcons[`${runway.id}-1`]}
              pane="markerPane"
              interactive={false}
            />
            <Marker
              position={[endpoints.end2.lat, endpoints.end2.lon]}
              icon={runwayIcons[`${runway.id}-2`]}
              pane="markerPane"
              interactive={false}
            />
          </span>
        )
      ))}

      {/* Parking Positions / Stands - only show when zoomed to stands level */}
      {zoomLevel === 'stands' && features.parkingPositions?.map(stand => (
        <Marker
          key={stand.id}
          position={[stand.latitude, stand.longitude]}
          icon={standIcons[stand.id]}
          pane="markerPane"
        >
          <Tooltip>
            <div className="airport-feature-tooltip">
              <strong>Stand {stand.ref || ''}</strong>
              {stand.name && <div>{stand.name}</div>}
            </div>
          </Tooltip>
        </Marker>
      ))}
    </>
  );
}

export default memo(AirportDetailOverlay);
